import { useLocation, useSearchParams } from "@/lib/router";

type QueryParams = {
  [key: string]: string;
};

type QueryParam = [string, string];

/**
 * `replace: true` rewrites the current history entry instead of pushing a new one.
 * Use it whenever the app corrects the URL on its own (stripping a param the user
 * can't act on, normalising a value) — pushing there leaves the stale URL one Back
 * press away, and the correcting effect just re-runs and pushes again, trapping the
 * user on the page. Push (the default) is for param changes the user initiated and
 * should be able to undo, e.g. opening a dialog.
 */
type QueryParamOptions = { replace?: boolean };

const useAppQueryParam = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const setQueryParam = (
    key: string,
    value: string,
    options?: QueryParamOptions,
  ) => {
    setSearchParams({ [key]: value }, options);
  };

  const addQueryParam = (
    key: string,
    value: string,
    options?: QueryParamOptions,
  ) => {
    setSearchParams((params) => {
      const newParams = new URLSearchParams(params);
      newParams.set(key, value);
      return newParams;
    }, options);
  };

  const addManyQueryParams = (
    queryParams: QueryParam[],
    options?: QueryParamOptions,
  ) => {
    setSearchParams((params) => {
      const newParams = new URLSearchParams(params);
      queryParams.forEach(([key, value]) => {
        newParams.append(key, value);
      });
      return newParams;
    }, options);
  };

  // const addManyQueryParams = (params: QueryParams) => {
  //   setSearchParams((prevParams) => {
  //     const newParams = new URLSearchParams(prevParams);
  //     Object.entries(params).forEach(([key, value]) => {
  //       newParams.set(key, value);
  //     });
  //     return newParams;
  //   });
  // };

  const removeQueryParam = (key: string, options?: QueryParamOptions) => {
    setSearchParams((params) => {
      const newParams = new URLSearchParams(params);
      newParams.delete(key);
      return newParams;
    }, options);
  };

  const removeManyQueryParams = (
    keys: string[],
    options?: QueryParamOptions,
  ) => {
    setSearchParams((params) => {
      const newParams = new URLSearchParams(params);
      keys.forEach((key) => {
        newParams.delete(key);
      });
      return newParams;
    }, options);
  };

  const getQueryParam = (key: string) => {
    return searchParams.get(key);
  };

  const getAllQueryParams = () => {
    const searchParams = new URLSearchParams(location.search);
    let params: QueryParams = {};
    for (let pair of searchParams.entries()) {
      params[pair[0]] = pair[1];
    }
    return params;
  };

  return {
    setQueryParam,
    getQueryParam,
    addQueryParam,
    addManyQueryParams,
    removeQueryParam,
    removeManyQueryParams,
    getAllQueryParams,
  };
};

export default useAppQueryParam;
